/**
 * Catalog domain-family grouping for the admin console.
 *
 * The family taxonomy is server-owned (`category_group` on each category, set via
 * migration 0008 + super_admin edits). This module only decides DISPLAY ORDER and
 * the catch-all bucket — it does NOT hardcode which catalog belongs where, so new
 * catalogs grouped on the backend appear without an SPA release.
 *
 * Any category whose `category_group` is null/empty/unknown falls into "Other",
 * which always sorts last.
 */
import type { CategoryOut } from "../auth/mastersApi";

export const OTHER_GROUP = "Other";

/**
 * Preferred ordering for known families (FRD-OXBO-001 catalog map). Groups not in
 * this list render after these, alphabetically, with "Other" always last — so a
 * future server-side family (e.g. "Materials") shows up gracefully pre-listing.
 */
export const GROUP_ORDER: readonly string[] = [
  "Drilling equipment",
  "Rig",
  "Casing / tubular",
  "Fluids & isolation",
  "Loop",
  "Survey",
  "Work tracking",
];

export interface CategoryGroup {
  group: string;
  categories: CategoryOut[];
}

function groupName(c: CategoryOut): string {
  const g = c.category_group?.trim();
  return g ? g : OTHER_GROUP;
}

function orderRank(group: string): number {
  if (group === OTHER_GROUP) return Number.MAX_SAFE_INTEGER;
  const i = GROUP_ORDER.indexOf(group);
  // Known groups first (in declared order); unknown server groups after, before Other.
  return i === -1 ? GROUP_ORDER.length : i;
}

/**
 * Bucket categories into families and return them in display order. Within a group,
 * categories keep their incoming order (the API returns them as stored); callers
 * may pre-sort if they want alphabetical.
 */
export function groupCategories(categories: CategoryOut[]): CategoryGroup[] {
  const buckets = new Map<string, CategoryOut[]>();
  for (const c of categories) {
    const g = groupName(c);
    const list = buckets.get(g);
    if (list) list.push(c);
    else buckets.set(g, [c]);
  }
  return [...buckets.entries()]
    .map(([group, cats]) => ({ group, categories: cats }))
    .sort((a, b) => {
      const ra = orderRank(a.group);
      const rb = orderRank(b.group);
      return ra !== rb ? ra - rb : a.group.localeCompare(b.group);
    });
}
